import React, { useState } from 'react';
import {  Phone, CheckCircle, TreePine, Zap, Home, Building2, Scissors, Leaf, Truck, MapPin } from 'lucide-react';
import { Header, Footer, useQuoteModal, usePageMeta } from '../components/SharedComponents';

const ServicesPage = () => {
  const { QuoteModal, openQuoteModal } = useQuoteModal();
  usePageMeta(
    'Tree Removal, Trimming & Emergency Services | One Choice Tree Service',
    'Professional tree removal, trimming & pruning, stump grinding, lot clearing, and 24/7 emergency storm response across the Baltimore, Maryland region.'
  );

  // Function to handle quote button clicks
  const handleQuoteClick = () => {
    openQuoteModal();
  };

  const [activeService, setActiveService] = useState(0);
  const services = [
    {
      id: 'removal',
      icon: TreePine,
      title: 'Tree Removal',
      subtitle: 'Safe and efficient removal',
      description: 'Safe and efficient removal of dangerous, diseased, or unwanted trees. Emergency storm damage cleanup available 24/7.',
      features: ['Hazardous tree removal', 'Storm damage cleanup', 'Stump grinding available', 'Complete debris removal'],
      image: 'https://images.pexels.com/photos/6218318/pexels-photo-6218318.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-red-500 to-orange-600',
      bgColor: 'bg-red-50'
    },
    {
      id: 'trimming',
      icon: Scissors,
      title: 'Tree Trimming & Pruning',
      subtitle: 'Professional pruning services',
      description: 'Professional pruning to maintain tree health, improve appearance, and ensure safety around structures.',
      features: ['Crown reduction', 'Deadwood removal', 'Structural pruning', 'Seasonal maintenance'],
      image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/6794577484929289205.jpg',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50'
    },
    {
      id: 'health',
      icon: Leaf,
      title: 'Tree Health & Care',
      subtitle: 'Comprehensive health services',
      description: 'Comprehensive tree health services to keep your trees thriving and your property beautiful.',
      features: ['Tree health assessments', 'Disease diagnosis', 'Preventive care programs', 'Expert consultations'],
      image: 'https://images.pexels.com/photos/5215651/pexels-photo-5215651.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-50'
    },
    {
      id: 'emergency',
      icon: Zap,
      title: 'Emergency Services',
      quoteService: 'Storm Damage / Emergency',
      subtitle: '24/7 emergency response',
      description: '24/7 emergency response for storm damage, fallen trees, and urgent safety hazards.',
      features: ['Storm damage response', 'Emergency tree removal', 'Power line clearance', 'Insurance claim assistance'],
      image: 'https://images.pexels.com/photos/1118873/pexels-photo-1118873.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-yellow-500 to-orange-600',
      bgColor: 'bg-yellow-50'
    },
    {
      id: 'commercial',
      icon: Building2,
      title: 'Commercial Services',
      subtitle: 'Specialized business solutions',
      description: 'Specialized tree care for businesses, municipalities, and large-scale properties.',
      features: ['Commercial property maintenance', 'Municipal contracts', 'Large-scale projects', 'Scheduled maintenance programs'],
      image: 'https://images.pexels.com/photos/269077/pexels-photo-269077.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'residential',
      icon: Home,
      title: 'Residential Services',
      subtitle: 'Personalized homeowner care',
      description: 'Personalized tree care services for homeowners throughout the Maryland area.',
      features: ['Backyard tree removal', 'Landscape enhancement', 'Property safety assessments', 'Neighbor-friendly practices'],
      image: 'https://images.pexels.com/photos/17208219/pexels-photo-17208219.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'crane',
      icon: Truck,
      title: 'Crane Services',
      subtitle: 'Heavy-duty tree removal',
      description: 'Professional crane services for challenging tree removals near buildings, power lines, and tight spaces.',
      features: ['Crane-assisted removal', 'High-reach capabilities', 'Precision placement', 'Complex access solutions'],
      image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/5584559015441101802.jpg',
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-50'
    },
    {
      id: 'lot-clearance',
      icon: MapPin,
      title: 'Lot Clearance',
      quoteService: 'Lot Clearing',
      subtitle: 'Complete land clearing',
      description: 'Full lot clearing services for construction, development, and land preparation projects.',
      features: ['Complete lot clearing', 'Selective clearing', 'Brush removal', 'Site preparation'],
      image: 'https://images.pexels.com/photos/18060323/pexels-photo-18060323.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-amber-500 to-yellow-600',
      bgColor: 'bg-amber-50'
    }
  ];

  const ServiceCard = ({ service, index, isActive }) => {
    // The whole card is the quote target, not just the button
    const openForThisService = () => {
      setActiveService(index);
      openQuoteModal(service.quoteService || service.title);
    };

    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Get a free quote for ${service.title}`}
        className={`relative overflow-hidden rounded-2xl transition-all duration-300 cursor-pointer ${
          isActive ? 'shadow-xl' : 'shadow-lg hover:shadow-xl'
        } bg-white hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2`}
        onClick={openForThisService}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openForThisService();
          }
        }}
      >
        {/* Image with overlay */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            loading="lazy"
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          
          {/* Service icon in corner */}
          <div className="absolute top-4 right-4 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
            <service.icon className="w-6 h-6 text-gray-700" />
          </div>

          {/* Title overlay on image */}
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-2xl font-bold text-white mb-1">
              {service.title}
            </h3>
            <p className="text-green-200 font-medium">{service.subtitle}</p>
          </div>
        </div>

        {/* Content - Always visible, larger text */}
        <div className="p-6">
          <p className="text-gray-700 text-base mb-6 leading-relaxed font-medium">
            {service.description}
          </p>

          {/* Features - Always visible, larger */}
          <div className="space-y-3 mb-6">
            {service.features.map((feature, idx) => (
              <div key={idx} className="flex items-center space-x-3">
                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.color}`}></div>
                <span className="text-gray-600 font-medium">{feature}</span>
              </div>
            ))}
          </div>

          {/* Kept as the visible affordance — the whole card carries the same
              action, so this is presentational and not separately focusable */}
          <div
            aria-hidden="true"
            className={`w-full bg-gradient-to-r ${service.color} text-white py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center space-x-3`}
          >
            <span>Get a Free Quote</span>
          </div>
        </div>
      </div>
    );
  };

  const InteractiveHero = () => {
    return (
      <div className="relative">
        <div className="text-center">
          <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <CheckCircle className="w-4 h-4 mr-2" />
            Residential & Commercial Tree Care
          </div>

          <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            We <span className="text-green-600">Remove</span> Trees.
            <br />
            <span className="text-green-600">You Get Results.</span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            From emergency storm cleanup to routine maintenance - professional tree care across Maryland.
          </p>

          {/* Simple CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleQuoteClick}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all duration-200"
            >
              Get a Free Quote
            </button>
            <a
              href="tel:+14432080747"
              className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors duration-200"
            >
              <span className="flex items-center justify-center space-x-2">
                <Phone className="w-5 h-5" />
                <span>Call (443) 208-0747</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      
      <Header currentPage="services" />
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-green-50 to-blue-50 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">

          {/* Contact Button */}
          <div className="mb-6 flex justify-end">
            <button
              onClick={handleQuoteClick}
              className="inline-flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors duration-200"
            >
              <span className="font-medium">Get Quote</span>
              <span className="text-lg">→</span>
            </button>
          </div>

          <InteractiveHero />
        </div>
      </section>

      {/* Services Gallery */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              OUR SERVICES
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Everything Your Trees Need
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From emergency storm cleanup to routine maintenance, we handle it all with precision and care.
            </p>
          </div>

          {/* Vertical Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard 
                key={service.id} 
                service={service} 
                index={index}
                isActive={activeService === index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              OUR WORK
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              See What We Do
            </h2>
            <p className="text-xl text-gray-600">
              Real projects, real results from across Maryland.
            </p>
          </div>

          {/* Horizontal Image Gallery */}
          <div className="relative">
            {/* Scroll hint */}
            <div className="flex items-center justify-center mb-6">
              <p className="text-gray-500 text-sm">← Swipe to see our work →</p>
            </div>

            {/* Gallery container */}
            <div 
              className="flex overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide gap-6"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              {[
                {
                  image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/2893782948520664233.jpg',
                  title: 'Tree Removal',
                  description: 'Safe removal of hazardous oak tree'
                },
                {
                  image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/6794577484929289205.jpg',
                  title: 'Professional Trimming',
                  description: 'Crown reduction and shaping'
                },
                {
                  image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/9167796078228255619.jpg',
                  title: 'Large Scale Removal',
                  description: 'Commercial property maintenance'
                },
                {
                  image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/5584559015441101802.jpg',
                  title: 'Precision Work',
                  description: 'Careful removal near structures'
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex-shrink-0 w-80 snap-center"
                >
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation arrows for desktop */}
            <div className="hidden md:block">
              <button 
                className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                onClick={() => {
                  const container = document.querySelector('.overflow-x-auto');
                  container.scrollBy({ left: -320, behavior: 'smooth' });
                }}
              >
                <span className="text-xl text-gray-700">←</span>
              </button>
              
              <button 
                className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                onClick={() => {
                  const container = document.querySelector('.overflow-x-auto');
                  container.scrollBy({ left: 320, behavior: 'smooth' });
                }}
              >
                <span className="text-xl text-gray-700">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Simple. Professional. Complete.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'You Call', desc: 'Tell us about your tree situation. We listen.' },
              { step: '02', title: 'We Assess', desc: 'Free estimate. No surprises. Clear timeline.' },
              { step: '03', title: 'Done Clean', desc: 'Tree removed. Yard perfect. You smile.' }
            ].map((item, index) => (
              <div key={index} className="relative group">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-2xl font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
                
                {/* Connecting line */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-green-200 transform translate-x-4 -translate-y-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Professional tree care across Maryland. Call today for your free estimate.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleQuoteClick}
              className="bg-gradient-to-r from-white to-emerald-50 hover:from-emerald-50 hover:to-emerald-100 text-green-700 px-12 py-4 rounded-xl font-bold text-xl shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              Get a Free Quote
            </button>
            <a
              href="tel:+14432080747"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-12 py-4 rounded-xl font-bold text-xl transition-all duration-300 transform hover:scale-105"
            >
              Call (443) 208-0747
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      {/* Add the QuoteModal component */}
      <QuoteModal />
    </div>
  );
};

export default ServicesPage;